import { defineStore, storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { editChannel, syncChannel } from "@/lib/broadcast-channels";
import { useReleaseStore } from "@/stores/release-store";
import { ClusterRole, useUserManager } from "@/stores/user-store";

// Only these roles may enter edit mode for stek content.
const EDIT_ROLES = [
    ClusterRole.USER,
    ClusterRole.STEK_MANAGER,
    ClusterRole.ORGANIZATION_MANAGER,
];

export const useEditStore = defineStore("edit", () => {
    const { currentOrganizationRoles } = useUserManager();
    const canEditInternal = ref(true);
    const canEdit = computed(() => {
        // Access release store lazily inside computed to avoid
        // initialization issues when this store is created first
        const activeRelease = useReleaseStore().activeRelease;
        return (
            canEditInternal.value &&
            currentOrganizationRoles.value.some((r) =>
                EDIT_ROLES.includes(r)
            ) &&
            !activeRelease
        );
    });
    const isEditing = ref(false);
    const isViewOnly = computed(
        () =>
            !currentOrganizationRoles.value.some((r) => EDIT_ROLES.includes(r))
    );

    const startEditing = async () => {
        if (canEdit.value) {
            isEditing.value = true;
            editChannel.postMessage("start");
        }
    };
    const stopEditing = () => {
        isEditing.value = false;
    };

    watch(isViewOnly, (newValue) => {
        if (newValue) {
            stopEditing();
        }
    });

    editChannel.addEventListener("message", (event) => {
        if (event.data === "start") {
            stopEditing();
        }
    });

    syncChannel.addEventListener("message", ({ data: { message } }) => {
        if (message === "queued") {
            canEditInternal.value = false;
        } else if (message === "synced") {
            canEditInternal.value = true;
        }
    });

    return { canEdit, isEditing, startEditing, stopEditing, isViewOnly };
});

export const useEditManager = () => storeToRefs(useEditStore());
